import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../../components/Table/Table";
import {
  getClassrooms,
  postClassroom,
  patchClassroom,
  deleteClassroom,
} from "../../../services/olympiad/classroomService";
import { getRegions } from "../../../services/olympiad/regionService";
import { getExamCenters } from "../../../services/olympiad/examCenterService";
import ClassroomPageHeader from "./PageHeader";
import ClassroomFilters from "./Filters";
import ClassroomForm from "./ClassroomForm";
import { useClassroomColumns } from "./columns";
import { toast } from "react-toastify";
import { Button } from "../../../components/forms/Buttons/Button";
import { UserContext } from "../../../utils/contexts/UserContext";
import bilAPI from "../../../services/api";

export default function Classrooms() {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  // Filters
  const [examCenterFilter, setExamCenterFilter] = useState(null);
  const [regionFilter, setRegionFilter] = useState(null);
  const [isFullFilter, setIsFullFilter] = useState(null);

  // Options
  const [regions, setRegions] = useState([]);
  const [examCenters, setExamCenters] = useState([]);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingClassroom, setEditingClassroom] = useState(null);

  const { user } = useContext(UserContext);
  const isExamCenterAdmin = user?.roles?.some(
    (role) => role.code === "EXAM_CENTER_ADMIN"
  );

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    exam_center: null,
    max_capacity: "",
  });

  useEffect(() => {
    if (isExamCenterAdmin) {
      return;
    }
    getRegions({})
      .then((res) => {
        setRegions(
          (res.results || []).map((r) => ({ label: r.name, value: r.uuid }))
        );
      })
      .catch((err) => console.error("Error fetching regions:", err));

    getExamCenters({})
      .then((res) => {
        setExamCenters(
          (res.results || []).map((ec) => ({
            label: ec.name,
            value: ec.uuid,
          }))
        );
      })
      .catch((err) => console.error("Error fetching exam centers:", err));
  }, []);

  const fetchData = () => {
    setLoading(true);
    getClassrooms({
      exam_center: examCenterFilter,
      region: regionFilter,
      is_full: isFullFilter,
      page: page,
      page_size: 50,
    })
      .then((res) => {
        setData(
          res.results.map((r) => ({
            ...r,
            occupied_seats: r.seat_count - r.available_seats,
          })) || []
        );
        setCount(res.count || 0);
        setPageCount(Math.ceil((res.count || 0) / 50));
      })
      .catch((err) => {
        console.error("Error fetching classrooms:", err);
        toast.error(t("admin.error_fetching_data"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [page, examCenterFilter, regionFilter, isFullFilter]);

  const handleCreate = () => {
    postClassroom({
      room_number: formData.name,
      exam_center_uuid: formData.exam_center,
      max_capacity: formData.max_capacity,
    })
      .then(() => {
        toast.success(t("admin.classroom_created"));
        setIsCreateModalOpen(false);
        setFormData({ name: "", exam_center: null, max_capacity: "" });
        fetchData();
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || t("admin.error_creating_classroom")
        );
      });
  };

  const handleEdit = (classroom) => {
    setEditingClassroom(classroom);
    setFormData({
      name: classroom.room_number || "",
      exam_center: classroom.exam_center?.uuid || null,
      max_capacity: classroom.max_capacity || "",
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    patchClassroom(editingClassroom.uuid, {
      room_number: formData.name,
      exam_center_uuid: formData.exam_center,
      max_capacity: formData.max_capacity,
    })
      .then(() => {
        toast.success(t("admin.classroom_updated"));
        setIsEditModalOpen(false);
        setEditingClassroom(null);
        setFormData({ name: "", exam_center: null, max_capacity: "" });
        fetchData();
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || t("admin.error_updating_classroom")
        );
      });
  };

  const handleDelete = (classroom) => {
    if (toast.isActive(classroom.uuid)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>
          {t("admin.confirm_delete_classroom", { classroom: classroom.name })}
        </p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={() => {
              toast.dismiss(classroom.uuid);
              deleteClassroom(classroom.uuid)
                .then(() => {
                  toast.success(t("admin.classroom_deleted"));
                  fetchData();
                })
                .catch((err) => {
                  toast.error(
                    err.response?.data?.detail ||
                      t("admin.error_deleting_classroom")
                  );
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(classroom.uuid)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: classroom.uuid }
    );
  };

  const columns = useClassroomColumns(handleEdit, handleDelete);

  return (
    <div className="p-6">
      <ClassroomPageHeader
        onCreateClick={() => setIsCreateModalOpen(true)}
        classrooms={data}
      />

      <ClassroomFilters
        isExamCenterAdmin={isExamCenterAdmin}
        examCenterFilter={examCenterFilter}
        setExamCenterFilter={setExamCenterFilter}
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
        isFullFilter={isFullFilter}
        setIsFullFilter={setIsFullFilter}
        regions={regions}
        examCenters={examCenters}
      />

      <Table
        columns={columns}
        data={data}
        loading={loading}
        count={count}
        pageNumber={page}
        pageCount={pageCount}
        nextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        previousPage={() => setPage((p) => Math.max(p - 1, 1))}
        gotoPage={(p) => setPage(p)}
      />

      {/* <ClassroomForm
        isOpen={isCreateModalOpen}
        isEdit={false}
        formData={formData}
        setFormData={setFormData}
        examCenters={examCenters}
        onClose={() => {
          setIsCreateModalOpen(false);
          setFormData({ name: "", exam_center: null, max_capacity: "" });
        }}
        onSubmit={handleCreate}
      />

      <ClassroomForm
        isOpen={isEditModalOpen}
        isEdit={true}
        formData={formData}
        setFormData={setFormData}
        examCenters={examCenters}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingClassroom(null);
          setFormData({ name: "", exam_center: null, max_capacity: "" });
        }}
        onSubmit={handleUpdate}
      /> */}
    </div>
  );
}
